import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/lib/constants";
import { 
  Phone, PhoneCall, Clock, Users, DollarSign, CheckCircle, ArrowRight,
  Bot, Zap, Calendar, MessageSquare, Shield, Headphones, Star,
  Building2, Wrench, Scissors, Scale, Stethoscope, Car, Home,
  Hammer, Thermometer, Bug, Paintbrush, Dumbbell, Leaf, FileText
} from "lucide-react";

const AIReceptionistPage = () => {
  const [schemas, setSchemas] = useState([]);

  useEffect(() => {
    document.title = "AI Receptionist Dallas | 24/7 AI Answering Service for Small Business | DFW";
    
    fetch(`${API}/ai-receptionist/schema`)
      .then(res => res.json())
      .then(data => setSchemas(data))
      .catch(() => {});
  }, []);

  const stats = [
    { value: "24/7", label: "Always Available", icon: Clock },
    { value: "Lower Cost", label: "Than Full-Time Staff", icon: DollarSign },
    { value: "100%", label: "Calls Answered", icon: PhoneCall },
    { value: "<3sec", label: "Response Time", icon: Zap }
  ];

  const industries = [
    { icon: Scale, name: "Attorneys & Law Firms", desc: "Client intake, appointment scheduling, case status updates", link: "/ai-receptionist-law-firms" },
    { icon: Dumbbell, name: "Gyms & Fitness Studios", desc: "Membership inquiries, class bookings, hours & pricing", link: "/ai-receptionist-gyms" },
    { icon: Wrench, name: "Plumbers", desc: "Emergency dispatching, service scheduling, quote requests", link: "/ai-receptionist-plumbers" },
    { icon: Home, name: "Roofers", desc: "Storm damage calls, free estimate scheduling, project updates", link: "/ai-receptionist-roofers" },
    { icon: Car, name: "Auto Body Shops", desc: "Repair estimates, appointment booking, status updates", link: "/ai-receptionist-auto-body" },
    { icon: Stethoscope, name: "Dentists & Medical", desc: "Patient scheduling, insurance verification, appointment reminders", link: "/ai-receptionist-dentists" },
    { icon: Thermometer, name: "HVAC Companies", desc: "Emergency repairs, maintenance scheduling, quote requests", link: "/ai-receptionist-hvac" },
    { icon: Hammer, name: "General Contractors", desc: "Project inquiries, estimate scheduling, subcontractor coordination", link: null },
    { icon: Bug, name: "Pest Control", desc: "Service scheduling, emergency calls, treatment follow-ups", link: null },
    { icon: Scissors, name: "Salons & Spas", desc: "Appointment booking, service inquiries, cancellations", link: "/ai-receptionist-salons" },
    { icon: Leaf, name: "Landscapers", desc: "Estimate requests, seasonal service scheduling, project updates", link: null },
    { icon: Paintbrush, name: "Painters", desc: "Quote requests, project scheduling, color consultations", link: null }
  ];

  const features = [
    { icon: Phone, title: "Never Miss a Call", desc: "Answer every call instantly, 24/7/365. No hold times, no voicemail, no missed opportunities." },
    { icon: Calendar, title: "Smart Scheduling", desc: "Book appointments directly into your calendar. Syncs with Google Calendar, Outlook, and more." },
    { icon: MessageSquare, title: "Lead Qualification", desc: "Capture caller information, qualify leads, and send detailed summaries to your team instantly." },
    { icon: Bot, title: "Natural Conversations", desc: "Advanced AI that sounds human, not robotic. Customized to your business voice and terminology." },
    { icon: Shield, title: "Emergency Routing", desc: "Intelligent escalation for urgent calls. Routes emergencies to on-call staff immediately." },
    { icon: Headphones, title: "Seamless Transfers", desc: "Warm transfers to your team with full context. Callers never have to repeat themselves." }
  ];

  const comparisons = [
    { item: "Full-time Receptionist", traditional: "$35,000-$50,000/year", ai: "$199-$499/month" },
    { item: "After-hours Coverage", traditional: "Overtime or none", ai: "Included 24/7" },
    { item: "Sick Days & Vacation", traditional: "15-20 days/year", ai: "Never" },
    { item: "Training Time", traditional: "2-4 weeks", ai: "Same day setup" },
    { item: "Multiple Calls", traditional: "Hold or missed", ai: "Unlimited simultaneous" },
    { item: "Spanish/Multilingual", traditional: "Extra cost", ai: "Included" }
  ];

  const faqs = [
    { 
      q: "What is an AI receptionist and how does it work?", 
      a: "An AI receptionist is a virtual phone answering system powered by artificial intelligence that handles incoming calls for your business 24/7. It uses natural language processing to understand callers, answer common questions, schedule appointments, capture lead information, and route urgent calls to your team. It sounds natural and human-like, not like a robot." 
    },
    { 
      q: "Can AI really replace a human receptionist?",
      a: "AI receptionists handle 85-95% of routine calls that a human receptionist would. They excel at answering FAQs, booking appointments, and capturing lead information. For complex situations or when callers request a human, the AI seamlessly transfers to your team with full context. Most businesses use AI to augment their team, not fully replace—handling after-hours and overflow calls while staff focus on high-value work."
    },
    { 
      q: "How much does an AI receptionist cost compared to hiring staff?",
      a: "A full-time receptionist in Dallas costs $35,000-$50,000/year plus benefits, training, and management time. AI receptionist services typically cost $199-$499/month—saving you 80-90% while providing 24/7 coverage. There's no overtime for nights/weekends, no sick days, and the AI can handle unlimited simultaneous calls."
    },
    { 
      q: "What industries benefit most from AI receptionists?",
      a: "Service-based businesses see the highest ROI: law firms, medical/dental offices, HVAC and plumbing companies, auto body shops, gyms, salons, contractors, roofers, pest control, and landscapers. Any business that relies on phone leads and appointment scheduling benefits significantly. The AI captures every potential customer, even at 2 AM."
    },
    { 
      q: "How long does it take to set up an AI receptionist?",
      a: "Most businesses are up and running same-day. Setup involves providing your business information, FAQs, service details, and call handling preferences. The AI learns your business instantly from your website and documents. No technical expertise needed—we handle the entire configuration for Dallas businesses."
    },
    { 
      q: "Will callers know they're talking to AI?",
      a: "Today's AI receptionists are remarkably natural. Many callers don't realize they're speaking with AI until told. The voice technology uses natural speech patterns, appropriate pauses, and can even handle interruptions gracefully. We customize the voice, tone, and personality to match your brand."
    }
  ];

  const steps = [
    { num: 1, title: "Discovery Call", desc: "We learn your business, call patterns, and specific needs" },
    { num: 2, title: "Custom Configuration", desc: "AI is trained on your FAQs, services, and call handling preferences" },
    { num: 3, title: "Integration Setup", desc: "Connect to your calendar, CRM, and phone system" },
    { num: 4, title: "Testing & Launch", desc: "Test calls, refine responses, go live with confidence" }
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="ai-receptionist-page">
      <Helmet>
        <title>AI Receptionist Dallas | 24/7 AI Answering Service for Small Business | DFW Virtual Receptionist</title>
        <meta name="description" content="Never miss a call again. AI receptionist for Dallas businesses - attorneys, plumbers, HVAC, dentists, gyms & more. 24/7 call answering, appointment scheduling & lead capture. Save 90% vs hiring staff. Free demo!" />
        <meta name="keywords" content="AI receptionist Dallas, AI answering service Dallas, virtual receptionist DFW, 24/7 answering service Dallas, AI phone answering, automated receptionist Texas, small business answering service, AI receptionist for attorneys, AI receptionist for plumbers, AI receptionist for dentists, virtual receptionist for small business" />
        <link rel="canonical" href="https://digitalmedianinja.com/ai-receptionist" />
        <meta property="og:title" content="AI Receptionist Dallas | 24/7 Virtual Receptionist for Small Business" />
        <meta property="og:description" content="Never miss a call. AI-powered receptionist handles calls 24/7 for Dallas attorneys, plumbers, dentists, gyms & more. Save 90% vs hiring staff." />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG" />
        <meta property="og:url" content="https://digitalmedianinja.com/ai-receptionist" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Receptionist Dallas | 24/7 AI Answering Service" />
        <meta name="twitter:description" content="AI receptionist for Dallas small businesses. Answer every call 24/7. Save 90% vs hiring." />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-transparent to-purple-600/10"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full">
                <Bot className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 text-sm font-medium">AI-Powered • Dallas-Based Support</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Never Miss a Call Again with <span className="text-blue-400">AI Receptionist</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-xl">
                Your 24/7 virtual receptionist that answers calls, books appointments, and captures leads—even at 3 AM. Perfect for Dallas attorneys, plumbers, dentists, gyms, and any business that can't afford to miss a call.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8" 
                  onClick={() => window.location.href='/contact'}
                  data-testid="ai-cta-hero"
                >
                  Get Free AI Receptionist Guide <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" /> Request Live Demo
                  </Button>
                </Link>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 border-2 border-black flex items-center justify-center text-white text-xs font-bold">
                      {['MT', 'JC', 'SC', 'RB'][i-1]}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                  </div>
                  <p className="text-sm text-gray-400">Trusted by Dallas businesses</p>
                </div>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="bg-gradient-to-br from-[#111] to-[#1a1a2e] rounded-3xl p-8 border border-white/10">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <PhoneCall className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Incoming Call</p>
                      <p className="text-sm text-gray-400">Crawford Plumbing - New Lead</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <p className="text-sm text-gray-400 mb-2">AI Response:</p>
                    <p className="text-white">"Good morning! Thank you for calling Crawford Plumbing. I can help you schedule a service appointment. Is this for a repair or routine maintenance?"</p>
                  </div>
                  
                  <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20">
                    <p className="text-sm text-green-400 mb-1">✓ Lead Captured</p>
                    <p className="text-white text-sm">John Smith • (214) 555-1234 • Leaky faucet repair</p>
                    <p className="text-xs text-gray-400 mt-1">Appointment: Tomorrow 10 AM - 12 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16">
            {stats.map((stat, i) => (
              <div key={i} className="text-center p-6 bg-white/5 rounded-xl border border-white/10 hover:border-blue-500/30 transition-colors">
                <stat.icon className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                <p className="text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-gray-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">AI Receptionist for Every Industry</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Whether you're a law firm, plumber, or dentist—our AI handles your industry's unique call patterns and terminology.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {industries.map((ind, i) => {
              const CardWrapper = ind.link ? Link : 'div';
              const cardProps = ind.link ? { to: ind.link } : {};
              return (
                <CardWrapper key={i} {...cardProps}>
                  <Card className="bg-[#111] border-white/10 hover:border-blue-500/30 transition-all group cursor-pointer h-full" data-testid={`industry-${i}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors">
                          <ind.icon className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
                            {ind.name}
                            {ind.link && <ArrowRight className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
                          </h3>
                          <p className="text-gray-500 text-sm">{ind.desc}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Everything a Great Receptionist Does—Powered by AI</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Natural conversations, instant responses, and 24/7 availability that human staff can't match.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat, i) => (
              <div key={i} className="p-6 bg-gradient-to-br from-[#111] to-[#0a0a0a] rounded-2xl border border-white/10 hover:border-blue-500/20 transition-all">
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                  <feat.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feat.title}</h3>
                <p className="text-gray-400">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Save 90% vs. Hiring a Receptionist</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A full-time receptionist in Dallas costs $35,000-$50,000/year. AI costs a fraction—with better coverage.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#111] rounded-2xl border border-white/10 overflow-hidden">
              <div className="grid grid-cols-3 gap-4 p-4 bg-white/5 border-b border-white/10">
                <div className="text-gray-400 font-medium"></div>
                <div className="text-center text-gray-400 font-medium">Human Receptionist</div>
                <div className="text-center text-blue-400 font-medium">AI Receptionist</div>
              </div>
              {comparisons.map((comp, i) => (
                <div key={i} className="grid grid-cols-3 gap-4 p-4 border-b border-white/5 last:border-0">
                  <div className="text-white font-medium">{comp.item}</div>
                  <div className="text-center text-gray-400">{comp.traditional}</div>
                  <div className="text-center text-green-400 font-medium">{comp.ai}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Live in Days, Not Weeks</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We handle the entire setup. Most Dallas businesses are answering calls with AI within 48 hours.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 border-2 border-blue-500 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-blue-400">{step.num}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400 text-sm">{step.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-blue-500/50 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="group bg-[#111] rounded-xl border border-white/10" data-testid={`faq-${i}`}>
                <summary className="p-6 cursor-pointer text-white font-medium flex items-center justify-between hover:bg-white/5 rounded-xl">
                  {faq.q}
                  <span className="text-blue-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-6 pb-6 text-gray-400">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Comprehensive Guides Section */}
      <section className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">In-Depth AI Receptionist Guides</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Explore our comprehensive guides to learn more about AI receptionists for your specific industry.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[
              { title: "AI Receptionist for Law Firms", desc: "Legal intake, case screening, emergency escalation", link: "/blog/ai-receptionist-for-attorneys-law-firms", color: "amber", readTime: "14 min" },
              { title: "AI Receptionist for Dentists", desc: "HIPAA-compliant booking, no-show reduction, insurance handling", link: "/blog/ai-receptionist-for-dentists-dental-practices", color: "cyan", readTime: "15 min" },
              { title: "AI Receptionist for HVAC", desc: "Seasonal surge handling, emergency dispatch, maintenance sales", link: "/blog/ai-receptionist-for-hvac-companies", color: "orange", readTime: "14 min" },
              { title: "Best AI Voice Technology", desc: "Compare AI voice solutions for receptionists", link: "/blog/best-ai-voice-virtual-receptionist", color: "blue", readTime: "12 min" },
              { title: "AI Receptionist for Salons", desc: "Appointment booking, stylist matching, no-show reduction", link: "/blog/ai-receptionist-for-salons-spas", color: "pink", readTime: "11 min" },
              { title: "AI Receptionist for Gyms", desc: "Membership inquiries, tour scheduling, class bookings", link: "/blog/ai-receptionist-for-gyms-fitness-centers", color: "green", readTime: "12 min" },
              { title: "AI Receptionist for Roofers", desc: "Storm surge handling, inspection scheduling", link: "/blog/ai-receptionist-for-roofers", color: "red", readTime: "12 min" },
              { title: "AEO for Local Businesses", desc: "Voice search & AI assistant optimization", link: "/blog/aeo-for-local-businesses-complete-guide", color: "indigo", readTime: "16 min" },
              { title: "GEO for Small Businesses", desc: "Get recommended by ChatGPT & AI search", link: "/blog/geo-for-small-businesses-ai-visibility-guide", color: "violet", readTime: "17 min" },
              { title: "AEO, GEO & SEO Guide", desc: "Modern search visibility strategy explained", link: "/blog/aeo-geo-seo-relationship-guide", color: "purple", readTime: "15 min" }
            ].map((guide, i) => (
              <Link key={i} to={guide.link}>
                <Card className={`bg-[#111] border-white/10 hover:border-${guide.color}-500/30 transition-all group cursor-pointer h-full`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <FileText className={`w-4 h-4 text-${guide.color}-400`} />
                      <span>{guide.readTime} read</span>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                      {guide.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4">{guide.desc}</p>
                    <span className="text-blue-400 text-sm flex items-center gap-1">
                      Read Guide <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Stop Missing Calls. Start Capturing Every Lead.
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join Dallas businesses using AI to answer calls 24/7. Get your free guide and see how much you could save.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8" 
              onClick={() => window.location.href='/contact'}
              data-testid="ai-cta-bottom"
            >
              Download Free AI Receptionist Guide
            </Button>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Schedule Free Demo Call
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIReceptionistPage;
